<template>
  <div class="flex flex-col justify-between flex-1 flex-grow">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form v-slot="{ values }"
              :initial-values="initial_values"
              :validation-schema="schema"
              class="mt-2 space-y-6"
              @submit="updateGoal">
          <div class="absolute flex items-center gap-2 top-4 right-6">
            <ToggleStatusButton
              :class="[{ 'mt-10': inPlaybook }]"
              :object="goal_displayed"
              @status-changed="
                ($event) => {
                  status = $event
                  $emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_CREATE_FORM, { ...goal_displayed, status: $event })
                  $emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_UPDATE_FORM, { ...goal_displayed, status: $event })
                }
              " />
            <div
              v-if="!inPlaybook && $route.name !== 'WorkspaceItem' && isModuleActivated(MODULES.WORKSPACE)"
              :class="[{ 'mt-3': goal.workspaces_shared_in?.length }]">
              <div type="button" class="primary-outline-btn w-fit" @click="shareGoal(goal)">
                <Share2 class="w-6 h-6 mr-1 transition ease-in-out duration-side-menu-transition-duration" />
                {{ $t("Share") }}
              </div>
              <div v-if="goal.workspaces_shared_in?.length" class="text-xs text-common-grey">
                {{ $t("Shared in") }} {{ goal.workspaces_shared_in?.length }} {{ $t("workspace") }}
              </div>
            </div>

            <div
              v-if="!inPlaybook"
              class="flex items-center justify-center w-8 h-8 ml-2 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
              @click="handleCloseGoal">
              <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ $t("Close") }}</span>
            </div>
          </div>

          <div class="flex gap-x-2">
            <div class="w-full">
              <div :class="{ 'flex gap-x-2': !inPlaybook }">
                <div class="w-full mt-1 -space-y-px rounded-md shadow-sm">
                  <TextInput
                    :disabled="editing_disabled"
                    autocomplete="on"
                    :autocomplete-options="resultReferencesListForText"
                    :auto-focus="true"
                    :is-box-background="true"
                    :show-label="true"
                    input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                    name="name"
                    type="text"
                    :value="goal.name"
                    :label="$t('Goal name')" />
                </div>

                <div :class="{ 'h-24 mt-2 w-[800px] ml-20': inPlaybook }" class="w-1/2 h-0 ml-6">
                  <CompaniesSelect
                    :short-king="inPlaybook"
                    :open-position="inPlaybook ? 'right' : 'center'"
                    :label="$t('Company/Opportunity')"
                    :disabled="isTemplate || editing_disabled"
                    :autocomplete-options="resultReferencesList"
                    :multi-select="inPlaybook ? true : false"
                    :companies="selected_company ? [selected_company] : selected_companies ? selected_companies : []"
                    @changed="
                      ($event) => {
                        handleSetConmpaniesList($event)
                      }
                    " />
                </div>
              </div>

              <div v-if="!editing_disabled" class="flex items-center mt-4 mb-0 gap-x-2">
                <Switch
                  v-model="goal_trigger"
                  :disabled="!selected_company"
                  :class="[
                    goal_trigger ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      goal_trigger && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : goal_trigger
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
                <span class="text-common-grey">{{
                  selected_company ? $t("Set your goal trigger") : $t("Select company to set your goal trigger")
                }}</span>
              </div>

              <div v-if="goal_trigger && (selected_company || selected_companies.length)">
                <AdvancedMetricsFilterForm
                  :object-type="selected_company ? selected_company.type : 'company'"
                  :initial-filter="goal.filter"
                  @update="handleUpdateFilter" />
              </div>

              <div class="mt-5 mb-5 rounded-md">
                <SelectInput
                  :disabled="editing_disabled"
                  label-attr="label"
                  value-attr="value"
                  :label="$t('Goal type')"
                  :is-box-background="true"
                  :show-label="true"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  name="type"
                  :allow-null="false"
                  :items="goal_types"
                  :placeholder="$t('Goal type')" />
                <p
                  v-if="goal_types.filter((x) => x.value == values['type']).length > 0"
                  class="p-2 text-sm leading-7 text-left text-gray-500">
                  {{ goal_types.filter((x) => x.value == values["type"])[0]["description"] }}
                </p>
              </div>

              <div class="flex w-full">
                <div class="w-1/2 mt-3 mb-5">
                  <TeamMembersSelect
                    v-model:selected-team-members="selected_users"
                    :autocomplete-options="autocompleteOptionsInPlaybook"
                    :disabled="editing_disabled || isTemplate"
                    :label="$t('Team members')" />
                </div>
                <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
                  <ContactsSelect
                    v-model:selected-contacts="selected_contacts"
                    :disabled="editing_disabled || isTemplate"
                    :label="$t('Contacts')"
                    :companies="[selected_company]" />
                </div>
              </div>

              <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
                <TextEditor
                  ref="quill"
                  v-model:content="goal_description"
                  :value="goal_description"
                  name="description"
                  :label="$t('Description')"
                  content-type="html"
                  input-classes="rounded-md"
                  :mention-items="resultReferencesListForText"
                  theme="bubble"
                  toolbar="essential" />
              </div>

              <div class="flex items-center gap-1 w-full mt-4">
                <div class="relative w-full">
                  <label
                    for="start_date"
                    class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                    :class="[
                      inputStartDateIsFocused || !!start_date ? 'top-0' : 'top-1/2',
                      isBoxBackgroud
                        ? 'bg-light-box-background dark:bg-dark-box-background'
                        : 'bg-white text-gray-400 dark:bg-dark-box-background',
                      inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                    ]">{{ $t("Start date") }}</label>
                  <div class="rounded-md shadow-sm">
                    <DatePicker
                      ref="start_date"
                      v-model="start_date"
                      :is-dark="$store.state.ui.darkMode"
                      class="bg-white dark:bg-dark-screen-background"
                      is-inline
                      mode="date"
                      :popover="{ visibility: 'click' }">
                      <template #default="{ inputValue, inputEvents }">
                        <div class="relative">
                          <svg
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>

                          <input
                            id="start_date"
                            autocomplete="off"
                            :value="inputValue"
                            class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                            :class="[
                              !isBoxBackgroud
                                ? 'bg-light-box-background dark:bg-dark-box-background'
                                : 'bg-light-screen-background dark:bg-dark-box-background'
                            ]"
                            @focusin="focusStartDateInput"
                            @focusout="focusStartDateInput"
                            v-on="inputEvents"
                            @input="
                              $utils.debounce(() => {
                                start_date = $event.target.value
                              }, 800)
                            ">
                        </div>
                      </template>
                      <template #footer>
                        <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                          <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                            {{ $t("Clear") }}
                          </button>

                          <button type="button" class="cancel-btn inline-block" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
                <div class="relative w-full">
                  <label
                    for="update_goal_d_date"
                    class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                    :class="[
                      !!relative_date_text || inputDueDateIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                      isBoxBackgroud
                        ? 'bg-light-box-background dark:bg-dark-box-background'
                        : 'bg-white text-gray-400 dark:bg-dark-box-background',
                      inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                    ]"
                    @click="is_modal_relative_date_open = true">{{ $t("Due date") }}</label>
                  <div class="rounded-md shadow-sm">
                    <DatePicker
                      ref="due_date"
                      v-model="due_date"
                      :is-dark="$store.state.ui.darkMode"
                      class="bg-white dark:bg-dark-screen-background"
                      is-inline
                      mode="date"
                      :popover="{ visibility: 'none' }">
                      <template #default="{ inputValue, inputEvents }">
                        <div class="relative">
                          <svg
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>

                          <input
                            id="update_goal_d_date"
                            autocomplete="off"
                            :value="inputValue ? inputValue : relative_date_text"
                            class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                            :class="[
                              !isBoxBackgroud
                                ? 'bg-light-box-background dark:bg-dark-box-background'
                                : 'bg-light-screen-background dark:bg-dark-box-background'
                            ]"
                            @focusin="focusDueDateInput"
                            @focusout="focusDueDateInput"
                            @click="is_modal_relative_date_open = true"
                            v-on="inputEvents"
                            @input="
                              $utils.debounce(() => {
                                due_date = $event.target.value
                              }, 800)
                            ">
                        </div>
                        <div v-if="is_modal_relative_date_open" class="absolute z-20">
                          <DateTimeFrame
                            class="mr-2 w-full bg-white dark:bg-dark-box-background rounded-lg"
                            :relative-date="relative_date"
                            @done="
                              ($event) => {
                                due_date = $event
                                is_modal_relative_date_open = false
                              }
                            "
                            @relative-date-changed="
                              ($event) => {
                                relative_date = $event
                                is_modal_relative_date_open = false
                              }
                            "
                            @cancel="is_modal_relative_date_open = false" />
                        </div>
                      </template>

                      <template #footer>
                        <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                          <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                            {{ $t("Clear") }}
                          </button>
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
              </div>

              <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-4 mb-4 mr-6 gap-x-2">
                <Switch
                  v-model="auto_set_start_date"
                  :class="[
                    auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : auto_set_start_date
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
                <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
              </div>

              <div v-if="inPlaybook" class="cursor-pointer" @click="assign_companies_to_tasks = !assign_companies_to_tasks">
                <span class="mr-2 mt-1 text-common-grey">{{ $t("Assign selected companies to tasks") }} </span>
                <input
                  :disabled="true"
                  :checked="assign_companies_to_tasks"
                  type="checkbox"
                  class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
              </div>
              <div v-if="inPlaybook" class="cursor-pointer" @click="assign_collaborators_to_tasks = !assign_collaborators_to_tasks">
                <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected team members to tasks") }} </span>
                <input
                  :disabled="true"
                  :checked="assign_collaborators_to_tasks"
                  type="checkbox"
                  class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
              </div>

              <div class="flex items-center mt-4 gap-x-2">
                <label class="relative text-common-grey top-4">{{ $t("Assigned tasks") }}:</label>

                <div
                  v-if="related_tasks_displayed.length || tasks_filter_active"
                  :class="{ 'pointer-events-none opacity-0': inPlaybook }"
                  class="ml-auto primary-btn w-fit"
                  @click="handleFilterTasks">
                  <Funnel class="w-5 h-5 mr-2 text-white" />{{ tasks_filter_active ? $t("Clear filter") : $t("Filter tasks") }}
                </div>

                <div
                  v-if="!isTemplate && !inPlaybook"
                  class="btn w-fit primary-btn"
                  :class="[`${tasks_filter_applied ? 'bg-gray-300' : ''} `]"
                  @click="!isTemplate && !inPlaybook ? handleCreateTask() : null">
                  {{ $t("Create New Task") }}
                </div>

                <Menu
                  v-if="!editing_disabled && (isTemplate || inPlaybook)"
                  v-slot="{ close }"
                  ref="goalsMenu"
                  as="div"
                  class="relative flex-shrink-0"
                  :class="[
                    `${!related_tasks_displayed.length && !tasks_filter_active ? 'ml-auto' : ''} ${
                      tasks_filter_applied ? 'pointer-events-none' : ''
                    } `
                  ]">
                  <MenuButton
                    class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                    <div class="btn primary-btn" :class="[`${tasks_filter_applied ? 'bg-gray-300' : ''} `]">
                      {{ $t("Add Task to Goal") }}
                    </div>
                  </MenuButton>
                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0">
                    <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                      <div
                        class="py-1 bg-white dark:bg-dark-screen-background p-3 border dark:border-dark-divider-border rounded-lg w-[340px]">
                        <div>
                          <div v-if="goal.template_id || isTemplate" class="flex w-full justify-end">
                            <div
                              :class="{ 'text-white bg-light-brand-blue': list_to_get == 'tasks' }"
                              class="p-1 px-3 rounded-full border cursor-pointer"
                              @click="list_to_get = 'tasks'">
                              {{ $t("Tasks") }}
                            </div>
                            <div
                              :class="{ 'text-white bg-light-brand-blue': list_to_get == 'templates' }"
                              class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                              @click="list_to_get = 'templates'">
                              {{ $t("Templates") }}
                            </div>
                          </div>

                          <AsyncAutoCompleteInput
                            :add="false"
                            :add-label="$t('Create Goal')"
                            :clear-on-select="true"
                            :is-box-background="true"
                            classes="w-full"
                            input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                            name="task"
                            value-attr="id"
                            :endpoint="
                              list_to_get == 'tasks'
                                ? `/api/organizations/${$store.state.organization.id}/tasks/${goal.id ? `?target_goal=${goal.id}` : ``}`
                                : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=goal`
                            "
                            type="text"
                            :placeholder="$t('Search for task to add')"
                            :debounce="500"
                            @chosen="
                              ($event) => {
                                handleTaskSelected($event, close)
                              }
                            " />
                        </div>
                        <div
                          v-if="!inPlaybook"
                          class="mt-3 mb-3 btn primary-btn"
                          @click="
                            () => {
                              close()
                              handleCreateTask()
                            }
                          ">
                          + {{ $t("Create New Task") }}
                        </div>
                      </div>
                    </MenuItems>
                  </transition>
                </Menu>
              </div>

              <div v-if="tasks_filter_active">
                <div class="grid items-center justify-center grid-cols-6 gap-x-4">
                  <div>
                    <MultiSelectInput
                      v-model:current-items="tasks_status"
                      name="tasks_status"
                      :items="$store.state.data?.task_statuses"
                      label-attr="name"
                      value-attr="value"
                      :allow-null="true"
                      :label="$t('Status')"
                      classes="w-full"
                      input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                      :selection-text="null"
                      :placeholder="$t('Status')"
                      :show-label="false" />
                  </div>

                  <div class="flex w-full ml-auto rounded-md shadow-sm">
                    <div class="relative flex items-stretch flex-grow focus-within:z-10">
                      <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <TextInput
                        :value="tasks_search_term"
                        input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                        name="task_name"
                        type="text"
                        :validate-on-input="true"
                        :label="$t('name')"
                        @update:modelValue="
                          (e) => {
                            tasks_search_term = e
                          }
                        " />
                    </div>
                  </div>

                  <div>
                    <AsyncMultiSelectCompaniesInput
                      :add-label="$t('Companies')"
                      :clear-on-select="true"
                      :is-box-background="true"
                      classes="w-full"
                      input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                      name="companies"
                      value-attr="id"
                      :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                      endpoint-method="post"
                      search-field="name"
                      type="text"
                      :placeholder="$t('Companies')"
                      @update:currentItems="
                        ($event) => {
                          handleTasksCompaniesSelected($event)
                        }
                      " />
                  </div>

                  <div>
                    <AsyncMultiSelectContactsInput
                      :add-label="$t('Contacts')"
                      :clear-on-select="true"
                      :is-box-background="true"
                      classes="w-full"
                      input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                      name="companies"
                      value-attr="id"
                      :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                      endpoint-method="post"
                      search-field="name"
                      type="text"
                      :placeholder="$t('Contacts')"
                      @update:currentItems="
                        ($event) => {
                          handleTasksContactsSelected($event)
                        }
                      " />
                  </div>

                  <div>
                    <MultiSelectInput
                      v-model:current-items="tasks_assigned_to"
                      name="tasks_assigned_to"
                      :items="$store.state.data?.users"
                      label-attr="full_name"
                      :allow-null="true"
                      value-attr="id"
                      :label="$t('Users')"
                      classes="w-full dark:text-dark-body-text"
                      input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                      selection-text="Members (%COUNT)"
                      :placeholder="$t('team members')"
                      :show-label="false" />
                  </div>

                  <div>
                    <DatePicker
                      v-model="tasks_due_date_range"
                      :is-dark="$store.state.ui.darkMode"
                      :input-debounce="500"
                      mode="range"
                      class="ml-auto bg-white dark:bg-dark-screen-background"
                      :attributes="[
                        {
                          key: 'today',
                          dot: true,
                          dates: new Date()
                        }
                      ]"
                      :popover="{ visibility: 'click' }"
                      is-range
                      range>
                      <template #default="{ inputValue, inputEvents, hidePopover }">
                        <div class="relative flex-grow">
                          <svg
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>

                          <button
                            v-if="tasks_due_date_range && tasks_due_date_range.start"
                            class="absolute right-0 flex items-center justify-center w-6 h-6 mx-2 rounded-full top-2 group hover:bg-gray-300 rounderd-full"
                            type="button"
                            @click="tasks_due_date_range = null">
                            <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
                          </button>

                          <input
                            :value="tasks_due_date_range && tasks_due_date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                            :placeholder="$t('Due date')"
                            class="w-full px-10 py-2 pl-8 pr-2 text-gray-900 bg-white border border-gray-300 rounded outline-none dark:bg-dark-box-background dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                            @blur="hidePopover"
                            v-on="inputEvents.start || inputEvents.end"
                            @input="
                              $utils.debounce(() => {
                                tasks_due_date_range = $event.target.value
                              }, 300)
                            ">
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
              </div>

              <div
                v-if="!related_tasks_displayed.length"
                class="relative w-full p-4 mb-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
                <div class="text-lg">
                  {{ $t("No tasks to display") }}
                </div>
                <div v-if="!tasks_filter_applied" class="mt-1 text-sm">
                  {{ $t("Add new tasks to goal") }}
                </div>
                <div v-else class="mt-1 text-sm">
                  {{ $t("No tasks matching filter") }}
                </div>
              </div>

              <draggable
                v-model="related_tasks_displayed"
                as="div"
                static
                :class="[
                  'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                  `grid ${full_screen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 overflow-visible mt-1 border-0 ${
                    $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                  } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
                ]"
                :component-data="{ type: 'transition-group' }"
                v-bind="{
                  disabled: tasks_count > tasksPerPage,
                  animation: 200,
                  group: 'group',
                  ghostClass: 'ghost',
                  chosenClass: 'chosen'
                }"
                item-key="id"
                @end="endTaskDrag()">
                <template #item="{ element: task }">
                  <div class="bg-white">
                    <div
                      v-if="expanded_tasks.includes(task.id)"
                      class="relative p-4 mb-4 bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(task)"
                      @mouseleave="handleTaskLeave">
                      <div class="flex items-center">
                        <ToggleStatusButton
                          :object="task"
                          :disable-toggle="task.filter"
                          @statusChanged="
                            ($event) => {
                              handleStatusChanged($event, task)
                            }
                          " />

                        <div class="ml-2">
                          {{ task.name }}
                        </div>
                        <div class="absolute flex -right-3 -top-3 gap-x-2">
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleExpandTask(task)">
                            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Minimize") }}</span>
                          </div>
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                            @click="handleViewTask(task)">
                            <Maximize2
                              class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                              color="#2DA7D7" />
                            <span
                              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                              {{ $t("View Task") }}</span>
                          </div>
                          <div
                            v-if="!editing_disabled"
                            class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleRemoveTask(task)">
                            <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Remove") }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex w-full mt-5">
                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey">
                              {{ $t("Priority") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                                <div>
                                  {{ task.priority }}
                                </div>
                                <div>
                                  <div v-if="task.priority == 'normal'">
                                    <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                                    <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                                  </div>
                                  <div v-else-if="task.priority == $t('high')">
                                    <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="task.priority == $t('highest')">
                                    <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="task.priority == $t('low')">
                                    <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                  <div v-else-if="task.priority == $t('lowest')">
                                    <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                </div>
                              </div>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey whitespace-nowrap">
                              {{ $t("Due Date") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <span v-if="task?.due_date">
                                {{ $moment(task?.due_date).format("DD MMM  YYYY") }}
                              </span>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div :class="[`${task.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                          <div
                            :class="[`${task.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                            class="mb-2 text-common-grey whitespace-nowrap text-start">
                            {{ $t("Team Members") }}
                          </div>
                          <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                            <span v-for="user in task.assigned_to.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                              <img
                                v-if="$utils.getUserById(user, 'avatar')"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="$utils.getUserById(user, 'avatar')"
                                alt="">
                              <Avatar
                                v-else
                                :first-name="$utils.getUserById(user, 'first_name')"
                                :last-name="$utils.getUserById(user, 'last_name')" />
                              <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                                <div class="">
                                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                    {{ $utils.getUserById(user, "full_name") }}
                                  </p>
                                </div>
                              </span>
                            </span>
                            <span
                              v-if="task.assigned_to.length > 4"
                              class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                              + {{ task.assigned_to.length - 4 }} {{ $t("more") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="relative flex p-4 mb-4 align-middle bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(task)"
                      @mouseleave="handleTaskLeave">
                      <div
                        :class="[
                          'w-4 h-4 rounded-full mt-1 mr-4 relative',
                          {
                            'bg-status-warning-yellow': getActualStatus(task.status) == 'overdue',
                            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),
                            'bg-status-success-green': task.status == 'completed',
                            'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                          }
                        ]" />
                      <div>
                        <button v-if="!(tasks_count > tasksPerPage)" class="button hover:cursor-move mr-2">
                          <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
                        </button>

                        {{ task.name }}
                      </div>

                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandTask(task)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Expand") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewTask(task)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Task") }}</span>
                        </div>
                        <div
                          v-if="!editing_disabled"
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveTask(task)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>

              <div class="flex items-center w-full !-mt-4">
                <div
                  v-if="tasks_count > 6 && tasks_count != tasksPerPage"
                  class="mr-2 text-sm text-blue cursor-pointer"
                  @click="tasksPerPage = tasks_count">
                  {{ $t("Show all") }} ({{ tasks_count }})
                </div>
                <div v-else-if="tasks_count > 6" class="mr-2 text-sm text-blue cursor-pointer" @click="tasksPerPage = 6">
                  {{ $t("Show less") }}
                </div>
                <Pagination
                  v-if="tasks_count > tasksPerPage"
                  class="ml-auto"
                  :total-pages="tasks_total_pages"
                  :current-page="tasksCurrentPage"
                  @input="tasksChangePage" />
              </div>

              <div class="relative rounded-md shadow-sm border p-2 mt-2">
                <div class="flex items-center justify-between w-full">
                  <label class="block text-common-grey">{{ $t("Tags") }}</label>
                  <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                    <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                    <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
                  </button>
                </div>
                <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
                  <div
                    v-for="tag in selected_tags"
                    :key="tag?.id"
                    class="flex focus:outline-none rounded-md"
                    :style="{ background: all_tags.find((t) => t.id == tag.id)?.color }">
                    <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                      <span
                        class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                        :style="{
                          background: all_tags.find((t) => t.id == tag.id)?.color
                        }">
                        <span class="block text-white truncate">{{ tag.name }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <AttachmentsField
                v-model="attachments"
                v-model:deleted-attachments="deleted_attachments"
                v-model:new-attachments="new_attachments"
                :disabled="editing_disabled"
                class="mt-4 mb-6" />

              <div v-if="!editing_disabled" class="flex flex-row justify-between w-full gap-2 mt-2">
                <button :class="{ 'w-1/2': inPlaybook }" type="button" class="w-1/3 cancel-btn" @click="handleCloseGoal">
                  {{ $t("Cancel") }}
                </button>
                <button
                  v-if="!inPlaybook"
                  type="button"
                  class="w-1/3 delete-btn"
                  @click="
                    $emitter.emit(ALL_EMITTERS.DELETE_OBJECT, {
                      object: { ...goal, type: 'Goal' },
                      objectDeleteCallBack: () => {
                        $emit('cancel')
                      }
                    })
                  ">
                  {{ $t("Delete") }}
                </button>
                <button
                  :disabled="submitting_is_on"
                  type="submit"
                  :class="{ 'w-1/2': inPlaybook }"
                  class="w-1/3 primary-btn"
                  @click="
                    () => {
                      !inPlaybook ? updateGoal(values) : null
                    }
                  ">
                  <RotateCw
                    :class="{
                      'animate-spin': submitting_is_on,
                      'opacity-100': submitting_is_on
                    }"
                    class="w-5 h-5 mr-3 -ml-1 opacity-0"
                    aria-hidden="true" />
                  <span class="mr-5">
                    {{ isTemplate ? $t("Save Goal as Template") : inPlaybook ? $t("Save Goal") : $t("Update Goal") }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Add tags -->
  <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note Deleted')">
    <template #form="{ close: closeModal }">
      <TagsAutoCompleteInput
        :placeholder="$t('Add new tag')"
        value-attr="full_name"
        :selected-tags="selected_tags"
        @changed="
          ($event) => {
            handleTagsChanged($event)
          }
        "
        @close="closeModal" />
    </template>
  </Modal>
</template>

<script>
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import { Form } from "vee-validate"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import { Switch } from "@headlessui/vue"
import { Maximize2, Funnel, ChevronsRight, ChevronRight, Share2, Search, X, Tally2, Plus, RotateCw } from "lucide-vue-next"
import Pagination from "@/components/shared/Pagination.vue"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import AsyncMultiSelectCompaniesInput from "@/components/shared/AsyncMultiSelectCompaniesInput.vue"
import AsyncMultiSelectContactsInput from "@/components/shared/AsyncMultiSelectContactsInput.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"
import dataService from "@/services/data.service"
import Avatar from "@/components/shared/Avatar.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import { MODULES } from "@/store/constants/references"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { useI18n } from "vue-i18n"
import TextEditor from "@/components/shared/TextEditor.vue"

import draggable from "vuedraggable"

export default {
  components: {
    Tally2,
    draggable,
    Modal,
    Plus,
    TagsAutoCompleteInput,
    TextEditor,
    DateTimeFrame,
    X,
    AdvancedMetricsFilterForm,
    AsyncAutoCompleteInput,
    MultiSelectInput,
    AsyncMultiSelectCompaniesInput,
    AsyncMultiSelectContactsInput,
    ToggleStatusButton,
    Menu,
    MenuButton,
    MenuItems,
    Pagination,
    ChevronRight,
    ChevronsRight,
    Funnel,
    Search,
    Share2,
    ContactsSelect,
    TeamMembersSelect,
    TextInput,
    SelectInput,
    Form,
    RotateCw,
    DatePicker,
    Switch,
    Avatar,
    Maximize2,
    CompaniesSelect,
    AttachmentsField
  },

  inject: ["canUserEditLayout", "isModuleActivated"],
  props: {
    resultReferencesList: null,
    resultReferencesListForText: null,
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    inPlaybook: {
      type: Boolean,
      default: false
    },

    isTemplate: {
      type: Boolean,
      default: false
    },
    goal: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel", "submitted", "error", "done"],
  setup(props) {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Goal name is required")),
      description: Yup.string(),
      type: Yup.string().required(t("Goal type is required")).nullable()
    })

    const goal_clone = { ...props.goal }

    let goal_name = goal_clone.name
    let type = goal_clone.type
    let description = goal_clone.description
    const initial_values = { name: goal_name, type, description }

    return {
      schema,
      goal_clone,
      initial_values
    }
  },
  data() {
    return {
      list_to_get: "tasks",
      auto_set_start_date: false,
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      status: "pending",
      assign_companies_to_tasks: false,
      assign_collaborators_to_tasks: false,
      goal_description: "",
      ALL_EMITTERS,
      MODULES,
      attachments: [],
      deleted_attachments: [],
      new_attachments: [],
      goal_trigger: false,
      selected_users: [],
      selected_contacts: [],
      due_date: null,
      start_date: null,
      relative_date: null,
      relative_date_text: null,
      inputDueDateIsFocused: false,
      inputStartDateIsFocused: false,
      is_modal_relative_date_open: false,
      is_filter_valid: false,
      submitting_is_on: false,
      filter: null,
      goal_types: [
        {
          label: this.$t("Specific"),
          value: "specific",
          description: this.$t("Set a specific goal such as “Book an upsell meeting with the decision maker and team at this company.”")
        },
        {
          label: this.$t("Measurable"),
          value: "measurable",
          description: this.$t("Set a measurable such as “Increase ARR with 100k.“")
        },
        {
          label: this.$t("Time-Bound"),
          value: "time-Bound",
          description: this.$t("Set a time-bound goal such as “Q4 100k expansion goal.“")
        }
      ],

      selected_company: null,
      selected_companies: [],
      goal_displayed: null,
      isBoxBackgroud: false,
      expanded_tasks: [],
      task_hover_id: null,

      tasksPerPage: 6,
      tasksCurrentPage: 1,
      related_tasks_displayed: [],
      tasks_total_pages: 1,
      tasks_count: 0,

      //tasks filters
      tasks_status: null,
      tasks_search_term: "",
      tasks_filter_active: false,
      tasks_companies: [],
      tasks_contacts: [],
      tasks_assigned_to: [],
      tasks_due_date_range: null,
      dont_fetch: false,
      full_screen: false
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false

      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },

    tasks_filter_applied() {
      if (
        this.tasks_status?.length ||
        this.tasks_search_term ||
        this.tasks_companies.length ||
        this.tasks_contacts.length ||
        this.tasks_assigned_to.length ||
        this.tasks_due_date_range
      ) {
        return true
      } else {
        return false
      }
    }
  },

  watch: {
    status() {
      if (this.status == "in-progress" && this.auto_set_start_date == true && !this.start_date) {
        this.start_date = new Date()
      }
    },
    tasksPerPage: {
      handler() {
        const params = { page_size: this.tasksPerPage, page: 1 }
        if (this.goal?.id) {
          dataService.getGoalTasks(this.goal?.id, params).then((res) => {
            this.tasksCurrentPage = 1
            this.related_tasks_displayed = res.data.results
            this.tasksTotalPages(res.data)
          })
        }
      }
    },

    //tasks filter watchers
    tasks_status: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },
    tasks_search_term: {
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },
    tasks_companies: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },
    tasks_contacts: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },

    tasks_assigned_to: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },
    tasks_due_date_range: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredGoalTasks()
        }
      }
    },
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date?.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date?.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date?.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date?.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date?.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    }
  },

  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  created() {
    this.register_listeners()

    if (this.goal?.auto_start_date_set) {
      this.auto_set_start_date = this.goal?.auto_start_date_set
    }

    if (this.inPlaybook) {
      const att_calc = []
      this.goal.attachments?.forEach((attachment_id) => {
        dataService.getAttachment(attachment_id).then((res) => {
          att_calc.push(res.data)
          if (att_calc.length == this.goal.attachments.length) {
            this.attachments = att_calc
          }
        })
      })
    } else if (this.goal.attachments) {
      this.attachments = this.goal.attachments.results
    }

    this.status = this.goal.status

    this.goal_description = this.goal.description
    if (this.goal.tags.length > 0) {
      this.all_tags = this.$store.state.data.actions_tags
      this.all_tags.map((tag) => {
        if (this.goal.tags.find((t) => t == tag.id)) {
          this.selected_tags.push(tag)
        }
      })
    } else {
      this.selected_tags = []
    }
    this.assign_companies_to_tasks = this.goal.assign_companies_to_tasks
    this.assign_collaborators_to_tasks = this.goal.assign_collaborators_to_tasks

    this.tasksTotalPages(this.goal.tasks)
    if (this.goal?.tasks?.results) {
      this.related_tasks_displayed = this.goal.tasks.results.slice(0, this.tasksPerPage)
    }

    if (this.inPlaybook && this.goal?.tasks) {
      this.related_tasks_displayed = []
      this.goal.tasks.forEach((task_id) => {
        dataService.getTask(task_id).then((res) => {
          this.related_tasks_displayed.push(res.data)
        })
      })
    }
    if (!this.isTemplate) {
      this.selected_users = this.goal.assigned_to
      this.selected_contacts = this.goal.contacts
      this.selected_company = this.goal.record
    }

    if (this.goal.companies) {
      this.selected_companies = []
      this.goal.companies.forEach((c) => {
        if (c.includes("$")) {
          this.selected_companies.push(c)
        } else {
          this.$store.dispatch("data/getRecord", c).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      })
      this.goal.companies
    }

    this.goal_displayed = this.goal

    if (this.goal.filter) {
      this.goal_trigger = true
    }

    this.filter = this.goal.filter
    if (this.goal.due_date) {
      this.due_date = this.$moment.utc(this.goal.due_date).local().toDate()
    }
    if (this.goal.start_date) {
      this.start_date = this.$moment.utc(this.goal.start_date).local().toDate()
    }
    if (
      this.goal.due_date_relative_to &&
      (this.inPlaybook ||
        (this.goal.due_date_relative_to == "start_date" && !this.goal.start_date) ||
        (this.goal.due_date_relative_to == "in_progress_date" && this.goal.status !== "in-progress"))
    ) {
      this.relative_date = {
        due_date_relative_to: this.goal.due_date_relative_to,
        due_date_days_offset: this.goal.due_date_days_offset
      }
    }
  },
  activated() {
    this.register_listeners()
  },

  mounted() {},

  methods: {
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },
    getActualStatus(object, status) {
      if (object.due_date) {
        if (
          (status == "pending" || status == "in-progress") &&
          this.$moment(object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          return "overdue"
        }
        return status
      }
      return status
    },

    handleStatusChanged(status, item) {
      item.status = status
    },

    endTaskDrag() {
      this.$store.dispatch("data/updateGoal", { goal: { id: this.goal.id, tasks: this.related_tasks_displayed.map((task) => task.id) } })
    },

    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])
      this.is_filter_valid = parsed_filter.valid

      this.filter = parsed_filter.filter
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_UPDATE_FORM, (tasks) => {
        this.handleTaskSelected(null, () => {}, tasks)
      })
      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_UPDATE_FORM, (task) => {
        let index = this.related_tasks_displayed.findIndex((t) => t.id == task.id)
        this.related_tasks_displayed.splice(index, 1, task)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_UPDATE_FORM)
      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_UPDATE_FORM)
    },
    shareGoal(goal) {
      this.$emitter.emit(ALL_EMITTERS.SHARE_OBJECT, {
        object: { ...goal, type: "Goal" },
        objectDeleteCallBack: () => {
          this.$emit("cancel")
        },
        show_confirmation: true
      })
    },
    handleRestoreStatus() {
      if (this.goal.status != this.goal_clone.status) {
        const data = {}
        data["goal"] = {
          id: this.goal?.id,
          record_full: this.goal.record,
          status: this.goal.status,
          dont_load: true
        }
        this.$store.dispatch("data/updateGoal", data).then(() => {})
      }
    },

    handleCloseGoal() {
      this.handleRestoreStatus()
      this.$emit("cancel")
    },
    handleSetConmpaniesList(e) {
      if (this.inPlaybook) {
        this.selected_companies = e
      } else {
        this.selected_company = e[0] ? e[0] : null
      }
      if (!e.length) {
        this.goal_trigger = false
      }
    },
    handleCreateTask() {
      this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
        inGoal: true,
        records: [this.selected_company]
      })
    },
    handleTasksCompaniesSelected(e) {
      this.tasks_companies = e
    },

    handleTasksContactsSelected(e) {
      this.tasks_contacts = e
    },

    getFilteredGoalTasks() {
      let params = {
        page_size: this.tasksPerPage,
        expand: "record",
        ...(!!this.tasks_assigned_to && {
          assigned_to: this.tasks_assigned_to.join(",")
        }),
        ...(!!this.tasks_status && { status: this.tasks_status.join(",") }),
        ...(!!this.tasks_companies?.length && {
          record: this.tasks_companies.map((c) => c.id).join(",")
        }),
        ...(!!this.tasks_contacts && {
          contacts: this.tasks_contacts.map((c) => c.id).join(",")
        })
      }
      // params["prevent_loading"] = true
      if (this.tasks_search_term) {
        params["name"] = this.tasks_search_term
      }
      params["goals"] = this.goal?.id
      if (this.tasks_due_date_range) {
        params["due_date_start"] = this.$moment(this.tasks_due_date_range.start).format("L")
        params["due_date_end"] = this.$moment(this.tasks_due_date_range.end).format("L")
      }
      this.$store.dispatch("data/getTasks", params).then((res) => {
        this.tasksTotalPages(res.data)
        this.related_tasks_displayed = res.data.results?.map((g) => {
          return {
            ...g,
            filter: g.filter?.split(" ")[0]
              ? g.filter.split(" ")[0].length > 1
                ? `( ${g.filter.slice(1, g.filter.lenght - 1)} )`
                : g.filter
              : g.filter
          }
        })
      })
    },

    handleFilterTasks() {
      this.tasks_filter_active = !this.tasks_filter_active
      if (!this.full_screen && this.tasks_filter_active) {
        // this.handleExpandPlan()
      }

      if (!this.tasks_filter_active && this.tasks_filter_applied) {
        this.tasksResetFilters()
      }
    },

    tasksResetFilters() {
      const params = { page_size: this.tasksPerPage, page: 1 }
      dataService.getGoalTasks(this.goal?.id, params).then((res) => {
        this.related_tasks_displayed = res.data.results
        this.tasksTotalPages(res.data)
      })
      this.dont_fetch = true
      setTimeout(() => {
        this.dont_fetch = false
      }, 500)
      this.tasks_status = null
      this.tasks_search_term = ""
      this.tasks_companies = []
      this.tasks_assigned_to = []
      this.tasks_due_date_range = null
      this.tasksCurrentPage = 1
    },

    tasksChangePage(page, calcTotalPages) {
      this.tasksCurrentPage = page
      const params = { page_size: this.tasksPerPage, page: page }
      dataService.getGoalTasks(this.goal?.id, params).then((res) => {
        this.related_tasks_displayed = res.data.results
        if (calcTotalPages) {
          this.tasksTotalPages(res.data)
        }
      })
    },

    tasksTotalPages(tasks) {
      this.tasks_total_pages = Math.ceil(parseInt(tasks?.count) / parseInt(this.tasksPerPage))
      this.tasks_count = tasks?.count
    },

    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },

    handleTaskSelected(task, close, tasks) {
      if (task.content) {
        task = task.content
        task.for_template = true
        if (task?.id) {
          task["id"] = this.$uuid()
        }
      }
      if (this.inPlaybook || this.isTemplate) {
        this.related_tasks_displayed.push(task)
      } else {
        dataService
          .addTaskToGoal(this.goal?.id, {
            tasks_ids: tasks ? tasks.map((t) => t.id) : [task.id]
          })
          .then(() => {
            this.tasksChangePage(1, true)
          })
        close()
      }
    },

    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },

    handleViewTask(task) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, { task: this.$utils.clone(task) })
    },

    handleRemoveTask(task) {
      dataService.removeTaskFromGoal(this.goal?.id, { tasks_ids: [task.id] }).then(() => {
        if (this.related_tasks_displayed.length <= 1 && this.tasksCurrentPage > 1) {
          this.tasksChangePage(this.tasksCurrentPage - 1, true)
        } else {
          this.tasksChangePage(this.tasksCurrentPage, true)
        }
      })
    },
    focusStartDateInput() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusDueDateInput() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    updateGoal(values) {
      let data = { ...values }
      let is_template_flag = false
      if (this.isTemplate && !this.goal.id) {
        this.submitting_is_on = true

        if (this.goal.for_template) {
          data["for_template"] = true
        }
        data["organization"] = this.$store.state.organization.id
        data["name"] = values.name
        data["source_object_id"] = this.goal?.id
        data["source_object_type"] = "goal"

        is_template_flag = true
      }
      this.submitting_is_on = true

      if (this.goal.for_template) {
        data["for_template"] = true
      }

      data["id"] = this.goal?.id
      data["organization"] = this.$store.state.organization.id
      data["assigned_to"] = this.selected_users?.map((x) => (x.id ? x.id : x.value ? x.value : x))
      data["contacts"] = this.selected_contacts?.map((x) => (x.id ? x.id : x))
      data["record"] = this.selected_company
      data["description"] = this.goal_description
      data["status"] = this.status == "overdue" ? "pending" : this.status
      data["auto_start_date_set"] = this.auto_set_start_date

      if (this.start_date) {
        data["start_date"] = this.$moment(this.start_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }
      data["tags"] = this.selected_tags.map((t) => t.id)
      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date?.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date?.due_date_days_offset
        data["due_date"] = null
      } else if (this.due_date) {
        data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }

      delete data["filter_metrics"]

      let post = null
      if (this.filter) {
        post = { goal: { ...data, filter: this.filter, record: data.record?.id } }
      } else {
        delete data.filter
        post = { goal: { ...data, record: data.record?.id } }
      }

      if (this.inPlaybook) {
        this.submitting_is_on = false

        const clean_tasks = []
        this.related_tasks_displayed.forEach((g) => {
          const task_clone = this.$utils.clone(g)
          task_clone["assigned_to"] = task_clone["assigned_to"].map((g) => (g.value ? g.value : g.id ? g.id : g))
          clean_tasks.push(task_clone.id)
        })

        if (this.relative_date) {
          data["due_date_relative_to"] = this.relative_date?.due_date_relative_to
          data["due_date_days_offset"] = this.relative_date?.due_date_days_offset
          data["due_date"] = null
        } else if (this.due_date) {
          data["due_date"] = this.$moment(this.due_date)
            .set("hour", this.$moment().hour())
            .set("minute", this.$moment().minute())
            .set("second", this.$moment().second())
            .toISOString()
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        } else {
          data["due_date"] = null
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        }

        data["companies"] = this.selected_companies.map((c) => (c.id ? c.id : c))
        data["tasks"] = clean_tasks

        if (this.filter) {
          data["filter"] = this.filter
        }

        data["assign_companies_to_tasks"] = this.assign_companies_to_tasks
        data["assign_collaborators_to_tasks"] = this.assign_collaborators_to_tasks

        data["status"] = this.status
        if (data["status"] == "overdue") {
          data["status"] = "pending"
        }

        if (this.attachments.length) {
          const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
          const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

          let attachment_calc = old_attachments.map((att) => att.id)

          if (new_attachments.length) {
            dataService.addAttachment({ attachments: new_attachments }).then((res) => {
              attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
              data["attachments"] = attachment_calc

              this.$emit("done", data)
            })
          } else {
            data["attachments"] = attachment_calc
            this.$emit("done", data)
          }
        } else {
          this.$emit("done", data)
        }
        this.$emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_UPDATE_FORM, data)
      } else {
        if (is_template_flag) {
          dataService.createTemplate({ ...data, content: data }).then((res) => {
            this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
            this.submitting_is_on = false
            this.$emit("done")
          })
        } else {
          if (this.goal.id && !this.goal.for_template) {
            this.$store
              .dispatch("data/updateGoal", post)
              .then((res) => {
                const params = { tasks_page_size: 6 }
                if (this.new_attachments.length) {
                  let formData = new FormData()
                  this.new_attachments.forEach((attachment, i) => {
                    Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                  })
                  this.$store.dispatch("data/addAttachmentsToGoal", {
                    id: res.id,
                    attachments: formData,
                    params
                  })
                }
                if (this.deleted_attachments.length) {
                  this.$store.dispatch("data/deleteAttachmentsFromGoal", {
                    id: res.id,
                    attachments_ids: this.deleted_attachments.map((x) => x.id),
                    params
                  })
                }

                this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, this.due_date ? new Date(this.due_date) : null)
                this.$emit("done", res)
                this.$emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_CREATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_UPDATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_AI_PROMPT, res)
                this.submitting_is_on = false
              })
              .catch(() => {
                this.submitting_is_on = false

                // To do show exceptions
                this.$emit("error")
              })
          } else {
            this.submitting_is_on = false
            this.$emit("done", data)
            this.$emitter.emit(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_CREATE_FORM, data)
          }
        }
      }
    }
  }
}
</script>
